/**
 * Admin Agents Console — control the 10-agent pipeline.
 *
 * Sits at /admin/agents. Top: master controls (pause, daily target, run-now,
 * recipient list). Grid of 10 agent cards. Conversations table with filters,
 * export buttons, and a detail modal with PDF/DOCX/JSON downloads.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Play, Pause, RefreshCcw, Send, X,
  FileDown, FileJson, FileText, FileSpreadsheet,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";

async function call(path, { method = "GET", body, raw = false } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (raw) return res;
  let payload = null;
  try { payload = await res.json(); } catch { /* empty */ }
  if (!res.ok) {
    const err = new Error(payload?.detail || `Request failed (${res.status})`);
    err.payload = payload;
    throw err;
  }
  return payload;
}

const fmtDt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
};

function Pill({ children, tone = "muted", testid }) {
  const styles = {
    muted: "text-[var(--text-muted)] border-[var(--border)]",
    accent: "text-[var(--accent)] border-[var(--accent)]",
    danger: "text-red-400 border-red-500/60",
  };
  return (
    <span
      data-testid={testid}
      className={`font-mono-ui text-[9px] tracking-[0.2em] uppercase px-2 py-1 border ${styles[tone]}`}
    >
      {children}
    </span>
  );
}

function HealthBadge({ health }) {
  // Compact live health pill for the admin header. Hover reveals the
  // detailed strip (key-pool + scheduler + p95). Click goes to the full
  // engine-health section on /admin.
  if (!health) return null;
  const state = health.health || "green";
  const dot = {
    green: "bg-emerald-400",
    amber: "bg-yellow-400",
    red: "bg-red-400",
  }[state] || "bg-slate-500";

  const keys = health.user_keys || {};
  const sched = health.agent_scheduler || {};
  const p95 = health.last_hour?.latency_p?.gemini?.p95;

  const subtitle = sched.master_paused
    ? "Paused"
    : sched.cooldown_active
    ? `Agents cool ${Math.max(1, Math.round((sched.cooldown_remaining_s || 0) / 60))}m`
    : `${keys.available ?? 0}/${keys.configured ?? 0} keys${p95 ? ` · p95 ${p95}ms` : ""}`;

  return (
    <Link
      to="/admin"
      data-testid={`agents-health-badge-${state}`}
      title={`System: ${state}. Click for full engine-health.`}
      className="group inline-flex items-center gap-2 px-2.5 py-1 border border-[var(--border)] hover:border-[var(--accent)] transition-colors"
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${dot} ${state === "green" ? "animate-pulse" : ""}`}
      />
      <span className="font-mono-ui text-[9px] tracking-[0.22em] uppercase text-[var(--text-muted)] group-hover:text-[var(--text)]">
        {subtitle}
      </span>
    </Link>
  );
}

const ALL_DOMAINS = [
  "startup_business", "corporate", "technology", "markets_finance", "personal_finance",
  "interpersonal_family", "legal", "geopolitical", "institutional", "crisis",
  "negotiation", "personal_development", "sales_commerce", "media_information",
  "social_movements", "military", "sports_performance", "personal_health",
  "operations", "biography_historical", "epics_literature",
];

function AgentCard({ agent, onToggle, onEdit, busy }) {
  const pct = agent.daily_target > 0
    ? Math.min(100, Math.round((agent.today_count / agent.daily_target) * 100))
    : 0;
  return (
    <div
      data-testid={`agent-card-${agent.agent_id}`}
      className={`border p-4 ${agent.enabled ? "border-[var(--border)]" : "border-[var(--border)] opacity-60"}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="font-ui text-sm text-[var(--text)] truncate">{agent.name}</div>
          <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-1">
            {agent.agent_id}
          </div>
        </div>
        <Pill tone={agent.enabled ? "accent" : "muted"} testid={`agent-state-${agent.agent_id}`}>
          {agent.enabled ? "ACTIVE" : "PAUSED"}
        </Pill>
      </div>

      {/* Per-agent daily progress bar */}
      <div className="mt-3 mb-1">
        <div className="flex items-baseline justify-between">
          <div className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--text-dim)]">today</div>
          <div className="font-mono-ui text-[10px] tracking-[0.18em] text-[var(--text-muted)]">
            <span data-testid={`agent-progress-${agent.agent_id}`} className="text-[var(--text)]">
              {agent.today_count}
            </span>
            {" / "}{agent.daily_target}
          </div>
        </div>
        <div className="h-1 bg-[var(--border-soft)] mt-1.5">
          <div
            data-testid={`agent-progress-bar-${agent.agent_id}`}
            className="h-1 bg-[var(--accent)] transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3">
        <div>
          <div className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--text-dim)]">total</div>
          <div className="font-serif text-lg text-[var(--text)]">{agent.runs_total.toLocaleString()}</div>
        </div>
        <div>
          <div className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--text-dim)]">datasets</div>
          <div className="font-serif text-lg text-[var(--text)]">{agent.datasets.length}</div>
        </div>
        <div>
          <div className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--text-dim)]">domains</div>
          <div className="font-serif text-lg text-[var(--text)]">{agent.domains.length}</div>
        </div>
      </div>

      <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-3 truncate">
        key: {agent.api_key_masked || "—"}
      </div>
      <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-1">
        Last: {fmtDt(agent.last_run_at)}
      </div>

      <div className="grid grid-cols-2 gap-2 mt-4">
        <button
          onClick={() => onToggle(agent)}
          disabled={busy}
          data-testid={`agent-toggle-${agent.agent_id}`}
          className="font-mono-ui text-[10px] tracking-[0.24em] uppercase py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors inline-flex items-center justify-center gap-2"
        >
          {agent.enabled ? <Pause size={11} /> : <Play size={11} />}
          {agent.enabled ? "Pause" : "Resume"}
        </button>
        <button
          onClick={() => onEdit(agent)}
          disabled={busy}
          data-testid={`agent-edit-${agent.agent_id}`}
          className="font-mono-ui text-[10px] tracking-[0.24em] uppercase py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors"
        >
          Edit
        </button>
      </div>
    </div>
  );
}

function EditAgentModal({ agent, onClose, onSaved }) {
  const [target, setTarget] = useState(String(agent.daily_target));
  const [domains, setDomains] = useState(new Set(agent.domains || []));
  const [saving, setSaving] = useState(false);

  const toggle = (d) => {
    const next = new Set(domains);
    if (next.has(d)) next.delete(d); else next.add(d);
    setDomains(next);
  };

  const onSave = async () => {
    const t = parseInt(target, 10);
    if (Number.isNaN(t) || t < 0 || t > 10000) {
      toast.error("Daily target must be between 0 and 10,000");
      return;
    }
    setSaving(true);
    try {
      await call(`/admin/agents/${agent.agent_id}`, {
        method: "PATCH",
        body: { daily_target: t, domains: Array.from(domains) },
      });
      toast.success("Agent updated.");
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div
      data-testid="agent-edit-modal"
      className="fixed inset-0 bg-[var(--text)]/70 z-50 flex items-stretch md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">
              Edit agent
            </div>
            <div className="font-serif text-lg text-[var(--text)] mt-1">{agent.name}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="agent-edit-close"
            className="text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
          >
            <X size={16} />
          </button>
        </header>
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)] block mb-2">
              Daily target
            </label>
            <input
              type="number"
              min="0"
              max="10000"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              data-testid="agent-edit-target"
              className="w-32 bg-[var(--bg-elevated)] border border-[var(--border)] px-3 py-2 font-mono-ui text-sm text-[var(--text)] focus:outline-none focus:border-[var(--accent)]"
            />
            <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-2">
              Conversations per UTC day. 100 = relaxed free-tier pace.
            </p>
          </div>
          <div>
            <label className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)] block mb-2">
              Categories (domains)
            </label>
            <div className="flex flex-wrap gap-1.5">
              {ALL_DOMAINS.map((d) => {
                const on = domains.has(d);
                return (
                  <button
                    key={d}
                    onClick={() => toggle(d)}
                    data-testid={`agent-edit-domain-${d}`}
                    className={`font-mono-ui text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 border transition-colors ${
                      on
                        ? "border-[var(--accent)] text-[var(--accent)]"
                        : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                    }`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-3">
              Datasets are auto-recomputed from the corpus when categories change.
            </p>
          </div>
        </div>
        <footer className="flex items-center justify-end gap-2 px-6 py-3 border-t border-[var(--border)]">
          <button
            onClick={onClose}
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="agent-edit-save"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-1.5 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 transition-colors"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function ConvDetailModal({ id, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    call(`/admin/agents/conversations/${id}`)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [id]);

  const dl = async (fmt) => {
    const res = await call(`/admin/agents/conversations/${id}/export?fmt=${fmt}`, { raw: true });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const cd = res.headers.get("Content-Disposition") || "";
    const m = /filename="([^"]+)"/.exec(cd);
    a.download = m ? m[1] : `conv.${fmt}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div
      data-testid="conv-modal"
      className="fixed inset-0 bg-[var(--text)]/70 z-50 flex items-stretch md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-3xl max-h-[100vh] md:max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <div className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[var(--text-muted)]">
            Agent conversation
          </div>
          <button
            onClick={onClose}
            data-testid="conv-modal-close"
            className="text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
          >
            <X size={16} />
          </button>
        </header>
        <div className="overflow-y-auto p-6">
          {err && <p className="text-red-400 font-mono-ui text-[11px]">{err}</p>}
          {!data && !err && (
            <p className="font-mono-ui text-[11px] tracking-[0.2em] uppercase text-[var(--text-muted)]">Loading…</p>
          )}
          {data && (
            <div>
              <div className="font-serif text-xl text-[var(--text)] mb-1">{data.agent_name}</div>
              <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)] mb-5">
                {fmtDt(data.started_at)} · {data.duration_ms} ms · {data.scenario_seed?.domain}
                {data.scenario_seed?.dataset_name && ` · ${data.scenario_seed.dataset_name}`}
              </div>
              {data.news_seed?.title && (
                <div className="border border-[var(--border)] p-3 mb-5 font-sans text-xs text-[var(--text-muted)]">
                  News seed: {data.news_seed.title}
                </div>
              )}
              {data.turns.map((t, i) => (
                <div key={i} className="mb-5">
                  <div className={`font-mono-ui text-[10px] tracking-[0.28em] uppercase mb-1.5 ${t.role === "user" ? "text-[var(--accent)]" : "text-[var(--text-muted)]"}`}>
                    {t.role}
                  </div>
                  <div className="font-sans text-sm text-[var(--text)] leading-relaxed whitespace-pre-wrap">
                    {t.content}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 px-6 py-3 border-t border-[var(--border)]">
          <button data-testid="dl-pdf" onClick={() => dl("pdf")} className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] inline-flex items-center gap-1.5">
            <FileText size={11} /> PDF
          </button>
          <button data-testid="dl-docx" onClick={() => dl("docx")} className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] inline-flex items-center gap-1.5">
            <FileSpreadsheet size={11} /> DOCX
          </button>
          <button data-testid="dl-json" onClick={() => dl("json")} className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] inline-flex items-center gap-1.5">
            <FileJson size={11} /> JSON
          </button>
        </footer>
      </div>
    </div>
  );
}

function LearnedCasesSection() {
  const [stats, setStats] = useState(null);
  const [minRating, setMinRating] = useState(4);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(null); // "together-sft" | "raw-jsonl" | null

  const load = () =>
    call("/admin/agents/learned-cases/stats")
      .then(setStats)
      .catch((e) => toast.error(e.message));

  useEffect(() => { load(); }, []);

  const eligible = useMemo(() => {
    if (!stats) return 0;
    let n = 0;
    for (const [r, count] of Object.entries(stats.by_rating || {})) {
      if (parseInt(r, 10) >= minRating) n += count;
    }
    return n;
  }, [stats, minRating]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const params = new URLSearchParams({
        fmt, min_rating: minRating, limit: 100000,
      });
      if (domain) params.set("domain", domain);
      const res = await call(`/admin/agents/learned-cases/export?${params.toString()}`, { raw: true });
      if (!res.ok) { toast.error("Export failed"); return; }
      const blob = await res.blob();
      // Count actual JSONL lines so the toast reflects what's truly in the
      // file (server drops rows with empty q/a defensively).
      let exported = 0;
      try {
        const text = await blob.text();
        exported = text.split("\n").filter(Boolean).length;
      } catch { /* empty */ }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : `hbm-learned-${fmt}.jsonl`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Exported ${exported.toLocaleString()} cases`);
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="py-10 border-t border-[var(--border)]" data-testid="learned-cases-section">
      <div className="grid md:grid-cols-[1fr_auto] gap-6 items-end mb-6">
        <div>
          <div className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[var(--accent)] mb-2">
            Fine-tune corpus
          </div>
          <h2 className="font-serif text-2xl text-[var(--text)] mb-2">
            Learned cases — production → fine-tune set
          </h2>
          <p className="font-sans text-sm text-[var(--text-muted)] max-w-2xl leading-relaxed">
            Every rating ≥ 4 conversation is promoted into <code className="font-mono-ui text-[12px]">agent_learned_cases</code>.
            Export them as Together AI / OpenAI SFT JSONL to refresh your
            fine-tune without touching code.
          </p>
        </div>
        <div className="text-right">
          <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">Total cases</div>
          <div data-testid="learned-total" className="font-serif text-5xl text-[var(--text)] leading-none">
            {stats?.total ?? "—"}
          </div>
          <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-2">
            last added {stats?.last_added_at ? fmtDt(stats.last_added_at) : "—"}
          </div>
        </div>
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-6">
            {[5, 4, 3, 2, 1, 0].map((r) => (
              <div
                key={r}
                data-testid={`learned-rating-${r}`}
                className="border border-[var(--border)] px-3 py-2"
              >
                <div className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
                  rating {r}
                </div>
                <div className="font-serif text-lg text-[var(--text)]">
                  {(stats.by_rating[r] || 0).toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          <div className="border border-[var(--border)] p-5">
            <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-4">
              Build export
            </div>
            <div className="grid md:grid-cols-[auto_auto_1fr_auto] gap-3 items-end mb-4">
              <div>
                <label className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] block mb-1.5">
                  Min rating
                </label>
                <select
                  value={minRating}
                  onChange={(e) => setMinRating(parseInt(e.target.value, 10))}
                  data-testid="learned-min-rating"
                  className="bg-[var(--bg)] border border-[var(--border)] px-3 py-2 font-mono-ui text-sm text-[var(--text)] focus:outline-none focus:border-[var(--accent)]"
                >
                  {[5, 4, 3, 2, 1].map((r) => (
                    <option key={r} value={r}>≥ {r}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] block mb-1.5">
                  Domain
                </label>
                <select
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  data-testid="learned-domain"
                  className="bg-[var(--bg)] border border-[var(--border)] px-3 py-2 font-mono-ui text-sm text-[var(--text)] focus:outline-none focus:border-[var(--accent)]"
                >
                  <option value="">All domains</option>
                  {(stats.by_domain || []).map((d) => (
                    <option key={d.domain} value={d.domain}>
                      {d.domain} ({d.count})
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-right md:text-left">
                <label className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] block mb-1.5">
                  Eligible
                </label>
                <div data-testid="learned-eligible" className="font-serif text-2xl text-[var(--accent)] leading-none mt-1">
                  {eligible.toLocaleString()}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => download("together-sft")}
                disabled={busy === "together-sft" || eligible === 0}
                data-testid="learned-dl-together"
                className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
              >
                <FileDown size={11} /> Together / OpenAI SFT JSONL
              </button>
              <button
                onClick={() => download("raw-jsonl")}
                disabled={busy === "raw-jsonl" || eligible === 0}
                data-testid="learned-dl-raw"
                className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
              >
                <FileJson size={11} /> Raw JSONL (DPO prep)
              </button>
            </div>
            <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-4">
              Together SFT format = OpenAI chat JSONL. Drop the file into
              Together&apos;s fine-tune wizard — no transform needed.
            </p>
          </div>

          <WeeklySnapshotsPanel />
        </>
      )}
    </section>
  );
}

function WeeklySnapshotsPanel() {
  const [snaps, setSnaps] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    call("/admin/agents/snapshots")
      .then(setSnaps)
      .catch((e) => toast.error(e.message));

  useEffect(() => { load(); }, []);

  const createNow = async () => {
    setBusy(true);
    try {
      const r = await call(
        "/admin/agents/snapshots/create?fmt=together-sft&min_rating=4&notify=true",
        { method: "POST" },
      );
      if (r.email_result?.ok) {
        toast.success(`Snapshot built (${r.line_count} cases) · email sent`);
      } else {
        toast.success(
          `Snapshot built (${r.line_count} cases) · email: ${r.email_result?.reason || "skipped"}`
        );
      }
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const download = async (id, filename) => {
    const res = await call(`/admin/agents/snapshots/${id}/download`, { raw: true });
    if (!res.ok) { toast.error("Download failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "snapshot.jsonl.gz";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="border border-[var(--border)] p-5 mt-4" data-testid="snapshots-panel">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--text-muted)] mb-1">
            Weekly snapshots
          </div>
          <p className="font-sans text-xs text-[var(--text-dim)] max-w-xl">
            Auto-generated every Sunday 04:00 UTC. Email goes to your digest
            recipients with a one-click download link. Run on-demand any time.
          </p>
        </div>
        <button
          onClick={createNow}
          disabled={busy}
          data-testid="snapshot-create-now"
          className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
        >
          {busy ? "Building…" : "Build snapshot now"}
        </button>
      </div>

      {snaps && snaps.length === 0 && (
        <p data-testid="snapshots-empty" className="font-sans text-sm text-[var(--text-dim)]">
          No snapshots yet. Click &ldquo;Build snapshot now&rdquo; or wait for Sunday.
        </p>
      )}

      <div className="divide-y divide-[var(--border-soft)]">
        {(snaps || []).map((s) => (
          <div
            key={s.id}
            data-testid={`snapshot-row-${s.id}`}
            className="py-3 flex flex-wrap items-center gap-3 first:pt-0 last:pb-0"
          >
            <div className="flex-1 min-w-0">
              <div className="font-ui text-sm text-[var(--text)] truncate">
                {fmtDt(s.created_at)} · {s.line_count.toLocaleString()} cases
              </div>
              <div className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-1 truncate">
                {s.fmt} · ≥{s.min_rating} · {Math.round(s.compressed_bytes / 1024)} KB gz · {s.storage}
                {s.email_result?.ok && " · email sent"}
                {s.email_result?.reason && ` · ${s.email_result.reason.slice(0, 60)}`}
              </div>
            </div>
            <button
              onClick={() => download(s.id, s.filename)}
              data-testid={`snapshot-dl-${s.id}`}
              className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] inline-flex items-center gap-1.5"
            >
              <FileDown size={11} /> Download
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminAgents() {
  const { user } = useAuth();
  const [search] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [convs, setConvs] = useState(null);
  const [health, setHealth] = useState(null);
  const [filter, setFilter] = useState({ agent_id: "", date: "", q: "" });
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [recipients, setRecipients] = useState("");
  const [openConvId, setOpenConvId] = useState(search.get("conv") || null);
  const [editAgent, setEditAgent] = useState(null);

  const agentMap = useMemo(() => {
    const m = {};
    (overview?.agents || []).forEach((a) => { m[a.agent_id] = a; });
    return m;
  }, [overview]);

  const loadOverview = () =>
    call("/admin/agents")
      .then((d) => {
        setOverview(d);
        setRecipients((d.master.digest_recipients || []).join(", "));
      })
      .catch((e) => toast.error(e.message));

  const loadConvs = (p = page) => {
    const params = new URLSearchParams({ page: p, page_size: 50 });
    if (filter.agent_id) params.set("agent_id", filter.agent_id);
    if (filter.date) params.set("date", filter.date);
    if (filter.q) params.set("q", filter.q);
    return call(`/admin/agents/conversations?${params.toString()}`)
      .then(setConvs)
      .catch((e) => toast.error(e.message));
  };

  useEffect(() => {
    if (user && user.role === "admin") {
      loadOverview();
      loadConvs(1);
      // Engine-health badge: refresh every 15s while this page is open.
      const fetchHealth = () =>
        call("/admin/engine-health?minutes=15")
          .then(setHealth)
          .catch(() => {});
      fetchHealth();
      const id = setInterval(fetchHealth, 15_000);
      return () => clearInterval(id);
    }
  }, [user]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: "/admin/agents" }} replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const onToggleAgent = async (a) => {
    setBusy(true);
    try {
      await call(`/admin/agents/${a.agent_id}`, { method: "PATCH", body: { enabled: !a.enabled } });
      await loadOverview();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const onToggleMaster = async () => {
    setBusy(true);
    try {
      await call("/admin/agents/config", { method: "POST", body: { paused: !overview.master.paused } });
      await loadOverview();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const onRunNow = async (count = 5) => {
    setBusy(true);
    try {
      await call(`/admin/agents/run-now?count=${count}`, { method: "POST" });
      toast.success(`Fired ${count} conversations`);
      await Promise.all([loadOverview(), loadConvs(1)]);
      setPage(1);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const onSaveConfig = async () => {
    const list = recipients.split(",").map((s) => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      await call("/admin/agents/config", {
        method: "POST",
        body: { digest_recipients: list },
      });
      toast.success("Saved.");
      await loadOverview();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const onSendDigest = async () => {
    setBusy(true);
    try {
      const r = await call("/admin/agents/digest/send-now", { method: "POST" });
      if (r.email?.ok) toast.success("Digest sent.");
      else toast.info(`Digest prepared. Email: ${r.email?.reason || "not configured"}`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const onBulkExport = async (fmt) => {
    setBulkBusy(true);
    try {
      const params = new URLSearchParams({ fmt, limit: 500 });
      if (filter.agent_id) params.set("agent_id", filter.agent_id);
      if (filter.date) params.set("date", filter.date);
      const res = await call(`/admin/agents/conversations-bulk-export?${params.toString()}`, { raw: true });
      if (!res.ok) { toast.error("Bulk export failed"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hbm-agent-chats-${fmt}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/admin"
          data-testid="agents-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2"
        >
          <ArrowLeft size={13} /> Admin
        </Link>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">AI Agents</h1>
        <div className="flex items-center gap-4">
          <HealthBadge health={health} />
          <Link
            to="/admin/evaluation"
            data-testid="agents-evaluation-link"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--accent)]"
          >
            Evaluation
          </Link>
          <button
            onClick={() => { loadOverview(); loadConvs(page); }}
            data-testid="agents-refresh"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--accent)] inline-flex items-center gap-2"
          >
            <RefreshCcw size={12} /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 pb-20">
        {/* Master strip */}
        <section className="py-10 border-b border-[var(--border)]">
          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-end">
            <div>
              <div className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[var(--accent)] mb-2">
                Pipeline
              </div>
              <h2 className="font-serif text-3xl text-[var(--text)] mb-2">
                25 agents talking to HBM around the clock.
              </h2>
              <p className="font-sans text-sm text-[var(--text-muted)] max-w-xl leading-relaxed">
                Each agent has its own dedicated Gemini key, draws from its
                assigned slice of the corpus + optional fresh Google News
                headlines. Default 100 conversations/day per agent (~2,500
                total). All chats stored, exportable, attachable to the daily
                digest.
              </p>
            </div>
            <div className="text-right">
              <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">Today</div>
              <div data-testid="today-total" className="font-serif text-5xl text-[var(--text)] leading-none">
                {overview?.today_total ?? "—"}
              </div>
              <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)] mt-2">
                target {overview?.master.daily_target ?? "—"}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-7">
            <button
              onClick={onToggleMaster}
              disabled={busy || !overview}
              data-testid="master-toggle"
              className={`font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border inline-flex items-center gap-2 transition-colors ${
                overview?.master.paused
                  ? "border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)]"
                  : "border-[var(--border)] text-[var(--text)] hover:border-red-500 hover:text-red-400"
              } disabled:opacity-40`}
            >
              {overview?.master.paused ? <Play size={12} /> : <Pause size={12} />}
              {overview?.master.paused ? "Resume pipeline" : "Pause pipeline"}
            </button>
            <button
              onClick={() => onRunNow(5)}
              disabled={busy}
              data-testid="run-now-5"
              className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors"
            >
              Run 5 now
            </button>
            <button
              onClick={() => onRunNow(20)}
              disabled={busy}
              data-testid="run-now-20"
              className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors"
            >
              Run 20 now
            </button>
            <button
              onClick={onSendDigest}
              disabled={busy}
              data-testid="send-digest"
              className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors inline-flex items-center gap-2"
            >
              <Send size={12} /> Send digest now
            </button>
          </div>
        </section>

        {/* Recipients */}
        <section className="py-8 border-b border-[var(--border)]">
          <div className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text-muted)] mb-3">
            Daily digest recipients
          </div>
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="comma-separated emails"
              data-testid="recipients-input"
              className="flex-1 min-w-[280px] bg-[var(--bg)] border border-[var(--border)] px-3 py-2 font-ui text-sm text-[var(--text)] placeholder:text-[var(--text-dim)] focus:outline-none focus:border-[var(--accent)]"
            />
            <button
              onClick={onSaveConfig}
              disabled={busy}
              data-testid="recipients-save"
              className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 transition-colors"
            >
              Save
            </button>
          </div>
          <p className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)] mt-3">
            Resend test-mode: recipient must be your verified Resend account email
            (<span className="text-[var(--accent)]">support@humanebehavior.com</span>).
            Verify a domain at resend.com/domains to email other addresses.
          </p>
        </section>

        {/* Agent grid */}
        <section className="py-10 border-b border-[var(--border)]">
          <div className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text-muted)] mb-5">
            Agents · {(overview?.agents.length ?? 0)}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(overview?.agents || []).map((a) => (
              <AgentCard
                key={a.agent_id}
                agent={a}
                onToggle={onToggleAgent}
                onEdit={setEditAgent}
                busy={busy}
              />
            ))}
          </div>
        </section>

        {/* Conversations */}
        <section className="py-10">
          <div className="flex items-center justify-between mb-5">
            <div className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text-muted)]">
              Conversations
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => onBulkExport("pdf")} disabled={bulkBusy} data-testid="bulk-pdf" className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 inline-flex items-center gap-1.5">
                <FileDown size={11} /> Bulk PDF
              </button>
              <button onClick={() => onBulkExport("docx")} disabled={bulkBusy} data-testid="bulk-docx" className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 inline-flex items-center gap-1.5">
                <FileDown size={11} /> Bulk DOCX
              </button>
              <button onClick={() => onBulkExport("json")} disabled={bulkBusy} data-testid="bulk-json" className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 inline-flex items-center gap-1.5">
                <FileDown size={11} /> Bulk JSON
              </button>
            </div>
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); setPage(1); loadConvs(1); }}
            className="grid md:grid-cols-[1fr_auto_auto_auto] gap-2 mb-5"
          >
            <input
              type="text"
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              placeholder="search within chats…"
              data-testid="conv-filter-q"
              className="bg-[var(--bg)] border border-[var(--border)] px-3 py-2 font-ui text-sm text-[var(--text)] focus:outline-none focus:border-[var(--accent)]"
            />
            <select
              value={filter.agent_id}
              onChange={(e) => setFilter({ ...filter, agent_id: e.target.value })}
              data-testid="conv-filter-agent"
              className="bg-[var(--bg)] border border-[var(--border)] px-3 py-2 font-ui text-sm text-[var(--text)] focus:outline-none focus:border-[var(--accent)]"
            >
              <option value="">All agents</option>
              {Object.values(agentMap).map((a) => (
                <option key={a.agent_id} value={a.agent_id}>{a.agent_id}</option>
              ))}
            </select>
            <input
              type="date"
              value={filter.date}
              onChange={(e) => setFilter({ ...filter, date: e.target.value })}
              data-testid="conv-filter-date"
              className="bg-[var(--bg)] border border-[var(--border)] px-3 py-2 font-ui text-sm text-[var(--text)] focus:outline-none focus:border-[var(--accent)]"
            />
            <button
              type="submit"
              data-testid="conv-filter-apply"
              className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-4 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
            >
              Apply
            </button>
          </form>

          <div className="border border-[var(--border)] divide-y divide-[var(--border-soft)]">
            {(convs?.conversations || []).map((c) => (
              <button
                key={c.id}
                onClick={() => setOpenConvId(c.id)}
                data-testid={`conv-row-${c.id}`}
                className="w-full text-left px-5 py-4 hover:bg-[var(--bg-elevated)] transition-colors flex flex-wrap items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-ui text-sm text-[var(--text)] truncate">
                    {c.preview || "(empty)"}
                  </div>
                  <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-1">
                    {c.agent_id} · {fmtDt(c.started_at)} · {c.duration_ms}ms · {c.domain || "—"}
                    {c.rating != null && ` · rating ${c.rating}/5`}
                    {c.promoted && " · promoted"}
                  </div>
                </div>
                <Pill tone="muted">{c.dataset_name || "—"}</Pill>
              </button>
            ))}
            {convs?.conversations.length === 0 && (
              <p
                data-testid="conv-empty"
                className="px-5 py-6 font-sans text-sm text-[var(--text-dim)]"
              >
                No conversations match these filters.
              </p>
            )}
          </div>

          {convs && convs.total > convs.page_size && (
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={() => { const p = Math.max(1, page - 1); setPage(p); loadConvs(p); }}
                disabled={page <= 1}
                data-testid="conv-prev"
                className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] disabled:opacity-40"
              >
                ← Prev
              </button>
              <span className="font-mono-ui text-[10px] tracking-[0.22em] text-[var(--text-muted)]">
                page {page} · {convs.total} total
              </span>
              <button
                onClick={() => { const p = page + 1; setPage(p); loadConvs(p); }}
                disabled={page * convs.page_size >= convs.total}
                data-testid="conv-next"
                className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          )}
        </section>

        <LearnedCasesSection />
      </main>

      {openConvId && <ConvDetailModal id={openConvId} onClose={() => setOpenConvId(null)} />}
      {editAgent && (
        <EditAgentModal
          agent={editAgent}
          onClose={() => setEditAgent(null)}
          onSaved={async () => { setEditAgent(null); await loadOverview(); }}
        />
      )}
    </div>
  );
}
